import type { WinchMqttTopics, WinchMqttUser } from './winchMqttTypes';

/**
 * Cloud MQTT topics from the Smartwinch Cloud/Portal API.
 * Live metrics come from telemetry. Named presets come from getposition.
 * Travel limits come from status `strictlimits`.
 */
export function winchMqttTopics(winchId: number | string): WinchMqttTopics {
    const base = `v1/winch/${winchId}`;
    return {
        publish: {
            getPosition: `${base}/getposition/request`,
            setPosition: `${base}/setposition/request`,
            goto: `${base}/gotoposition/request`,
            stop: `${base}/stop/request`,
            status: `${base}/status/request`,
            verticalScanEnable: `${base}/vertical_scan/enable/request`,
            verticalScanDisable: `${base}/vertical_scan/disable/request`,
            verticalScanSetLimits: `${base}/vertical_scan/setlimits/request`,
            verticalScanStatus: `${base}/vertical_scan/status/request`,
        },
        subscribe: {
            online: `${base}/online`,
            telemetry: `${base}/telemetry`,
            getPosition: `${base}/getposition/response`,
            setPosition: `${base}/setposition/response`,
            goto: `${base}/gotoposition/response`,
            stop: `${base}/stop/response`,
            status: `${base}/status/response`,
            verticalScanEnable: `${base}/vertical_scan/enable/response`,
            verticalScanDisable: `${base}/vertical_scan/disable/response`,
            verticalScanSetLimits: `${base}/vertical_scan/setlimits/response`,
            verticalScanStatus: `${base}/vertical_scan/status/response`,
        },
    };
}

export function createWinchRequestId(): string {
    if (typeof globalThis.crypto !== 'undefined' && globalThis.crypto.randomUUID) {
        return globalThis.crypto.randomUUID();
    }
    return `winch-${Date.now()}`;
}

function withUser(user: WinchMqttUser): { uuid: string; user: WinchMqttUser } {
    return {
        uuid: createWinchRequestId(),
        user,
    };
}

export function buildGetPositionPayload(user: WinchMqttUser): string {
    return JSON.stringify(withUser(user));
}

export function buildWinchStatusPayload(user: WinchMqttUser): string {
    return JSON.stringify(withUser(user));
}

export function buildSetPositionCommand(input: {
    user: WinchMqttUser;
    measurement: { distance: number; depth: number };
}): { uuid: string; payload: string } {
    const meta = withUser(input.user);
    return {
        uuid: meta.uuid,
        payload: JSON.stringify({
            ...meta,
            position: {
                measurement: input.measurement,
            },
        }),
    };
}

export function buildSetPositionPayload(input: {
    user: WinchMqttUser;
    measurement: { distance: number; depth: number };
}): string {
    return buildSetPositionCommand(input).payload;
}

export function buildGotoPositionPayload(input: {
    user: WinchMqttUser;
    position: 'measurement' | 'service';
}): string {
    return JSON.stringify({
        ...withUser(input.user),
        position: input.position,
    });
}

export function buildStopPayload(user: WinchMqttUser): string {
    return JSON.stringify(withUser(user));
}

export function buildVerticalScanStatusCommand(user: WinchMqttUser): {
    uuid: string;
    payload: string;
} {
    const meta = withUser(user);
    return { uuid: meta.uuid, payload: JSON.stringify(meta) };
}

export function buildVerticalScanStatusPayload(user: WinchMqttUser): string {
    return buildVerticalScanStatusCommand(user).payload;
}

export function buildVerticalScanEnableCommand(user: WinchMqttUser): {
    uuid: string;
    payload: string;
} {
    const meta = withUser(user);
    return { uuid: meta.uuid, payload: JSON.stringify(meta) };
}

export function buildVerticalScanEnablePayload(user: WinchMqttUser): string {
    return buildVerticalScanEnableCommand(user).payload;
}

export function buildVerticalScanDisablePayload(user: WinchMqttUser): string {
    return JSON.stringify(withUser(user));
}

export function buildVerticalScanSetLimitsCommand(input: {
    user: WinchMqttUser;
    x: number;
    yMin: number;
    yMax: number;
}): { uuid: string; payload: string } {
    const meta = withUser(input.user);
    return {
        uuid: meta.uuid,
        payload: JSON.stringify({
            ...meta,
            x: input.x,
            yMin: input.yMin,
            yMax: input.yMax,
        }),
    };
}

export function buildVerticalScanSetLimitsPayload(input: {
    user: WinchMqttUser;
    x: number;
    yMin: number;
    yMax: number;
}): string {
    return buildVerticalScanSetLimitsCommand(input).payload;
}
